import BaseModal from 'components/BaseModal';
import React, { useState, useRef, forwardRef, useImperativeHandle } from "react";
import { BASE_PADDING } from "constants/Layout";
import { Form, Col, Row, Typography, Button, Alert, Tabs} from "antd";
import { CopyOutlined, CheckOutlined } from '@ant-design/icons';
import ProductAPI from 'restAPI/ProductAPI';
import SubProductAPI from 'restAPI/SubProductAPI';
import { CodeIconLarge } from 'components/Icons';
import { WidgetEmpty } from 'components/widgets/WidgetEmpty';
import ComingSoonTag from 'components/ComingSoonTag';
import Vocabulary from 'components/Vocabulary';
import { FieldTypes, generateFormFields } from 'utils/formUtils';
import { GITHUBACTIONSSNIPPET, GITHUBACTIONSARMORCODESNIPPET } from '../jenkins/ToolsScript';
import { Environments } from 'views/Findings/Constants';
import useUpdateEffect from 'hooks/useUpdateEffect';
import { useHasDisabledEnvironment } from 'contexts/GlobalSettingsContext';
import _ from 'lodash';

const {Text, Link} = Typography;
const {TabPane} = Tabs;

const GithubActionsFormPopup = forwardRef((props, ref) => {
    const [data, setData] = useState(() => props.initialValues || {});
    const [form] = Form.useForm();
    const thisRef = useRef();
    const [codeGenerated, setCodeGenerated] = useState();
    const selectedMenuKey = props.selectedMenuKey;
    const {productId, subProductId, environment, additionalAQLFilters} = data
    const {hasDisabledEnvironment, envValue} = useHasDisabledEnvironment();
    useImperativeHandle(ref, () => thisRef.current);
    useUpdateEffect(() => {
        if(productId) {
            form.setFieldsValue({subProductId: null, environment: null})
        }
    },[productId, form])

    useUpdateEffect(() => {
        if(subProductId) {
            form.setFieldsValue({environment: null})
        }
    },[subProductId, form])

    const baseModalProps = {
        title: <>Configure</>,
        footer: null
    }

    const generateCode = async () => {
        setCodeGenerated(false)
        try {
            await form.validateFields();
            setCodeGenerated(true);
        } catch(err) {
            console.log(err)
        }
    }

    const getCode = () => {
        let _envValue = environment;
        const mode = form.getFieldValue('mode');
        if(hasDisabledEnvironment){
            _envValue = envValue;
        }

        const scriptKey = selectedMenuKey === "githubActionsArmorcode" ? "GithubActionsArmorcode" : "GithubActionsScript";

        const scriptString = scripts[scriptKey].replace('{product}', productId.label).replace('{subProduct}', subProductId.label).replace('{envName}', _envValue).replace('buildvalidation', 'build').replace('{mode}', mode);
        
        if (_.isEmpty(additionalAQLFilters)) {
            return scriptString.replace('{additionalAQLFilters}', "");
        } else {
            return scriptString.replace('{additionalAQLFilters}', additionalAQLFilters);
        }
    };

    const codeTab = (
        <Tabs defaultActiveKey="1">
            <TabPane tab="YAML" key="1" style={{fontSize: '10px'}}>
                {codeGenerated ?
                <Typography.Text
                    copyable={{
                        text: getCode(),
                        tooltips: false,
                        icon: [<Button  style={{position: "absolute", top: "50px", right: "0"}} key="copy-icon" icon={<CopyOutlined />} type="link">Copy</Button>, <Button  style={{position: "absolute", top: "50px", right: "0"}} key="copied-icon" icon={<CheckOutlined />} type="link">Copied</Button>],
                    }}
                >
                    <pre>
                        {getCode()}
                    </pre>
                </Typography.Text>
                : <WidgetEmpty description={"Code will appear here once you click on generate button."} icon={<CodeIconLarge />}  />}
            </TabPane>
            <TabPane tab={<>Python <ComingSoonTag /></>} key="2">
                <WidgetEmpty description={"Code will appear here once you click on generate button."} icon={<CodeIconLarge />}  />
            </TabPane>
        </Tabs>
    )

    const fields = [
        {
            name: "productId",
            label: <Vocabulary name="product" />,
            type: FieldTypes.SELECT,
            colSpan: 12,
            showSearch: true,
            required: true,
            props: {
                api: ProductAPI.getAllQuick,
                labelInValue: true,
                autoFocus: true
            }
        },
        {
            name: "subProductId",
            label: <Vocabulary name="subProduct" />,
            type: FieldTypes.SELECT,
            colSpan: 12,
            showSearch: true,
            required: true,
            props: {
                labelInValue: true,
                ...(productId?.value ? {
                    api: SubProductAPI.getAllQuick,
                    apiParams: productId?.value,
                } : {})
                
            }
        },
        ...(hasDisabledEnvironment ? [] : [{
            name: "environment",
            label: "Environment",
            type: FieldTypes.SELECT,
            colSpan: 12,
            showSearch: true,
            options: Environments,
            required: true,
        }]),
        ...(
            [
              ...(selectedMenuKey === "githubActionsArmorcode"
                ? [
                    {
                      name: "mode",
                      label: "Mode",
                      type: FieldTypes.SELECT,
                      colSpan: 12,
                      showSearch: true,
                      options: [
                        { name: "Warn", id: "warn" },
                        { name: "Block", id: "block" }
                      ],
                      required: true,
                    },
                    {
                        name: "additionalAQLFilters",
                        label: "Additional AQL Filters",
                        colSpan: 24,
                        help: "Enter additional ArmorCode Query Language (AQL) filters to further refine the scope of findings used for release gate decisions. These filters will be applied in conjunction with the existing scope defined in the release management page."
                      }
                  ]
                : []),
            ]
          ),
    ]

    return (
        <BaseModal
            modalProps={baseModalProps}
            ref={ref}
            afterClose={props.afterClose}
        >
            {selectedMenuKey === "githubActionsArmorcode" ? (
                <>
                  <Text >
                   Enter the details below to generate the ArmorCode GitHub Action step, then insert into your GitHub Actions workflow. 
                  </Text>
                  <div style={{ marginTop: 10 }}>
                    Please refer to the 
                    <Link
                    href="/api/jwt/zendesk/sso?return_to=https://support.armorcode.com/hc/en-us/articles/19600562378003-GitHub-Actions"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ marginLeft: 5, marginRight: 5 }}
                  >
                    GitHub Actions Documentation
                  </Link>
                    for more information.
                  </div>
                </>
              )  : (
                <Text>
                    Enter the details and click on generate button to generate the code.
                    Select desired scripting language from tab.
                </Text>
            )}

            <Form form={form} layout="vertical" onValuesChange={(_, values) => setData({...data, ...values})} initialValues={props.initialValues}>
                <Row className='p-t-l'>
                    <Col span={24}>
                        {generateFormFields(fields)}
                        <Form.Item className="text-right">
                            <Button type="primary" onClick={generateCode}>Generate</Button>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
            <Row size={BASE_PADDING}>
                <Col span={24}>
                    <Alert description={codeTab} type="info" />
                </Col>
            </Row>
            {codeGenerated &&
                <Row className="m-y-sm">
                    <Col span={24}>
                        <Alert message={<Text>If you don't have an API token, generate it from <Link href="#/integrations/ArmorCode%20API">here</Link></Text>} type="success" />
                    </Col>
                </Row>
             }
        </BaseModal>
    )
})

const scripts = {
    GithubActionsArmorcode: GITHUBACTIONSARMORCODESNIPPET,
    GithubActionsScript: GITHUBACTIONSSNIPPET,
}

export default GithubActionsFormPopup;